import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { Expense, CreateExpenseRequest, CategoryTreeNode } from '../types';
import { apiClient } from '../api/client';
import { formatAmountForInput, parseAmountFromInput } from '../utils/currency';
import CategorySelect from './CategorySelect';

interface ExpenseFormProps {
  expense?: Expense | null; // For editing existing expense
  onExpenseAdded?: (expense: Expense) => void;
  className?: string;
}

const ExpenseForm: React.FC<ExpenseFormProps> = ({ expense, onExpenseAdded, className = "" }) => {
  const [formData, setFormData] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
    description: '',
    category_id: null as number | null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Populate form data when expense prop changes (for editing)
  useEffect(() => {
    if (expense) {
      setFormData({
        amount: formatAmountForInput(expense.amount),
        date: expense.date,
        description: expense.description || '',
        category_id: expense.category.id,
      });
    } else {
      // Reset form for new expense
      setFormData({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        description: '',
        category_id: null,
      });
    }
    setErrors({});
  }, [expense]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.amount.trim()) {
      newErrors.amount = 'Amount is required';
    } else {
      const amount = parseAmountFromInput(formData.amount);
      if (amount <= 0) {
        newErrors.amount = 'Amount must be greater than 0';
      }
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const amount = parseAmountFromInput(formData.amount);

      const expenseData = {
        amount,
        date: formData.date,
        description: formData.description.trim() || undefined,
        category_id: formData.category_id!,
      };

      let resultExpense: Expense;

      if (expense) {
        // Update existing expense
        resultExpense = await apiClient.updateExpense(expense.id, expenseData);
      } else {
        // Create new expense
        resultExpense = await apiClient.createExpense(expenseData);
      }

      // Reset form only when creating new expense
      if (!expense) {
        setFormData({
          amount: '',
          date: new Date().toISOString().split('T')[0],
          description: '',
          category_id: null,
        });
      }
      setErrors({});

      // Notify parent component
      if (onExpenseAdded) {
        onExpenseAdded(resultExpense);
      }
    } catch (error: any) {
      console.error(`Failed to ${expense ? 'update' : 'create'} expense:`, error);
      setErrors({ submit: error.message || `Failed to ${expense ? 'update' : 'create'} expense` });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, amount: value }));

    // Clear amount error when user starts typing
    if (errors.amount) {
      setErrors(prev => ({ ...prev, amount: '' }));
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, date: e.target.value }));

    // Clear date error when user selects a date
    if (errors.date) {
      setErrors(prev => ({ ...prev, date: '' }));
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, description: e.target.value }));
  };

  const handleCategoryChange = (categoryId: number | null) => {
    setFormData(prev => ({ ...prev, category_id: categoryId }));

    // Clear category error when user selects a category
    if (errors.category_id) {
      setErrors(prev => ({ ...prev, category_id: '' }));
    }
  };

  return (
    <div className={`bg-white/5 rounded-lg border border-white/10 p-6 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <Plus className="h-5 w-5 text-blue-400" />
        <h3 className="text-lg font-semibold text-white">
          {expense ? 'Edit Expense' : 'Add New Expense'}
        </h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Amount */}
        <div>
          <label htmlFor="amount" className="block text-sm font-medium text-slate-300 mb-2">
            Amount (INR) *
          </label>
          <input
            type="text"
            id="amount"
            value={formData.amount}
            onChange={handleAmountChange}
            placeholder="0.00"
            className={`w-full px-3 py-2 bg-white/5 border rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent-gold)] ${
              errors.amount ? 'border-red-500' : 'border-white/10 hover:border-white/20'
            }`}
          />
          {errors.amount && (
            <p className="mt-1 text-sm text-red-400">{errors.amount}</p>
          )}
        </div>

        {/* Date */}
        <div>
          <label htmlFor="date" className="block text-sm font-medium text-slate-300 mb-2">
            Date *
          </label>
          <input
            type="date"
            id="date"
            value={formData.date}
            onChange={handleDateChange}
            className={`w-full px-3 py-2 bg-white/5 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-[var(--accent-gold)] ${
              errors.date ? 'border-red-500' : 'border-white/10 hover:border-white/20'
            }`}
          />
          {errors.date && (
            <p className="mt-1 text-sm text-red-400">{errors.date}</p>
          )}
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Category *
          </label>
          <CategorySelect
            value={formData.category_id || undefined}
            onChange={handleCategoryChange}
            placeholder="Select a category"
            className={errors.category_id ? 'border-red-500' : ''}
          />
          {errors.category_id && (
            <p className="mt-1 text-sm text-red-400">{errors.category_id}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-slate-300 mb-2">
            Description
          </label>
          <input
            type="text"
            id="description"
            value={formData.description}
            onChange={handleDescriptionChange}
            placeholder="Optional description"
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent-gold)] hover:border-white/20"
          />
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className="p-3 bg-red-500/20 border border-red-500/50 text-red-300 rounded-md">
            {errors.submit}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full px-4 py-2 bg-[var(--accent-gold)] hover:bg-[#d4b896] disabled:bg-[var(--accent-gold)]/50 disabled:cursor-not-allowed text-white rounded-md transition-colors font-medium"
        >
          {isSubmitting
            ? (expense ? 'Updating...' : 'Adding Expense...')
            : (expense ? 'Update Expense' : 'Add Expense')
          }
        </button>
      </form>
    </div>
  );
};

export default ExpenseForm;